import React, { useState } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import Icon from '../../../components/AppIcon';

const CategoryBreakdownChart = ({ data, currency, isPro }) => {
  const [activeIndex, setActiveIndex] = useState(null);

  const COLORS = [
    'var(--color-primary)',
    'var(--color-accent)',
    'var(--color-success)',
    'var(--color-warning)',
    'var(--color-error)',
    '#8B7AF8',
    '#10B981',
    '#F59E0B',
    '#6366F1',
    '#EF4444'
  ];

  const onPieEnter = (_, index) => {
    setActiveIndex(index);
  };

  const onPieLeave = () => {
    setActiveIndex(null);
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload?.length) {
      const item = payload?.[0]?.payload;
      return (
        <div className="bg-card p-12 rounded-lg shadow-elevation-3 border border-border">
          <p className="text-sm font-caption font-medium text-card-foreground mb-4">
            {payload?.[0]?.name}
          </p>
          <p className="text-lg font-heading font-semibold text-primary">
            {currency}{payload?.[0]?.value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
          <p className="text-xs text-muted-foreground mt-4">
            {payload?.[0]?.payload?.percentage?.toFixed(1)}% of total
          </p>
          {isPro && item?.change !== undefined && (
            <p className={`text-xs mt-4 font-semibold ${
              item?.change > 0 ? 'text-error' : item?.change < 0 ? 'text-success' : 'text-muted-foreground'
            }`}>
              {item?.change > 0 ? '+' : ''}{item?.change?.toFixed(1)}% vs last month
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  const CustomLegend = ({ payload }) => {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mt-16 px-8">
        {payload?.map((entry, index) => (
          <div 
            key={`legend-${index}`}
            className="flex items-center p-8 rounded-lg hover:bg-muted transition-smooth cursor-pointer"
            onMouseEnter={() => setActiveIndex(index)}
            onMouseLeave={() => setActiveIndex(null)}
          >
            <div 
              className="w-12 h-12 rounded-full flex-shrink-0"
              style={{ backgroundColor: entry?.color }}
            />
            <div className="ml-8 flex-1 min-w-0">
              <p className="text-sm font-caption font-medium text-card-foreground truncate">
                {entry?.value}
              </p>
              <p className="text-xs text-muted-foreground">
                {currency}{entry?.payload?.value?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </p>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="bg-card rounded-lg p-12 md:p-16 lg:p-20 shadow-elevation-2">
      <div className="flex items-center mb-12">
        <Icon name="PieChart" size={20} color="var(--color-primary)" strokeWidth={2.5} />
        <h3 className="ml-12 text-base md:text-lg lg:text-xl font-heading font-semibold text-card-foreground">
          Expense Breakdown by Category
        </h3>
      </div>
      <div className="w-full h-[280px] sm:h-[320px] md:h-[360px]" aria-label="Category Expense Breakdown Pie Chart">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              labelLine={false}
              outerRadius="60%"
              fill="#8884d8"
              dataKey="value"
              onMouseEnter={onPieEnter}
              onMouseLeave={onPieLeave}
            >
              {data?.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={COLORS?.[index % COLORS?.length]}
                  opacity={activeIndex === null || activeIndex === index ? 1 : 0.6}
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend content={<CustomLegend />} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default CategoryBreakdownChart;